'use client';

import { type CSSProperties, type MouseEvent, memo, type ReactNode, type Ref, useId, useLayoutEffect, useMemo } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { useToggle } from '@interxfi/design-system/util/toggle';
import ArrowLeftIcon from '../../../icons/react/icon-arrow-left';
import CollapseIcon from '../../../icons/react/icon-collapse';
import ExpandIcon from '../../../icons/react/icon-expand';
import XLargeIcon from '../../../icons/react/icon-x-large';
import type { Space } from '../../../styles/tokens';
import { usePrefersReducedMotion } from '../../../util/accessibility';
import { buildResponsiveProps, useBreakpoint } from '../../../util/responsive';
import { sanitizeCSSVariables } from '../../../util/style';
import { Button } from '../../button/button';
import { TypographyProviderReset } from '../../typography/context';
import { DialogHeader } from '../header/dialog-header';
import { DRAWER_DATA_ATTRIBUTE_NAME, findPinchableDialogs, useMergedDialogOpenProps } from '../utils';
import dialogStyles from '../dialog.module.scss';
import styles from './drawer.module.scss';

const dataAttrProps = { [DRAWER_DATA_ATTRIBUTE_NAME]: true } as const;

const drawerContentTransition = {
  duration: 0.4,
  ease: [0.16, 1, 0.3, 1],
} as const;

const overlayTransition = {
  duration: 0.2,
  ease: [0.16, 1, 0.3, 1],
} as const;

const overlayAnimationVariants = {
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  initial: { opacity: 0 },
} as const;

export const DEFAULT_NESTED_DRAWER_PROPS = {
  closeIcon: <ArrowLeftIcon aria-hidden />,
  withoutAnimation: true,
  withoutOverlay: true,
} as const;

// handleCloseMouseDown prevents the dialog from shifting positions when the close button is clicked on,
// e.g., when a required input that had focus is blurred and renders an error message, shifting
// dialog content up and moving the close button
function handleCloseMouseDown(e: MouseEvent<HTMLButtonElement>) {
  e.preventDefault();
  e.stopPropagation();
}

export function findParentDrawer(element: HTMLElement | null): HTMLElement | null {
  if (!element) {
    return null;
  }

  if (element.matches(`[${DRAWER_DATA_ATTRIBUTE_NAME}]`)) {
    return element;
  }

  return findParentDrawer(element.parentElement);
}

const componentStyleName = 'drawer';

const defaultCloseIcon = <XLargeIcon aria-hidden />;

export type DrawerDescription = { rendered: ReactNode } | string;

export interface DrawerProps {
  canExpand?: boolean;
  children?: ReactNode;
  closeIcon?: ReactNode;
  closeIconAction?: VoidFunction;
  description: DrawerDescription;
  dialogClassName?: string;
  gap?: Space;
  isOpen?: boolean;
  modal?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  progress?: number; // number between 0 and 1
  ref?: Ref<HTMLDivElement>;
  style?: CSSProperties;
  title?: ReactNode;
  titleRightAction?: ReactNode;
  trigger: ReactNode;
  withoutAnimation?: boolean;
  withoutOverlay?: boolean;
}

export const Drawer = memo(
  ({
    canExpand = false,
    children,
    closeIcon = defaultCloseIcon,
    closeIconAction,
    description,
    dialogClassName,
    gap = 4,
    modal,
    isOpen: providedIsOpen,
    onOpenChange: providedOnOpenChange,
    progress,
    ref,
    style,
    title,
    titleRightAction,
    trigger,
    withoutAnimation = false,
    withoutOverlay = false,
    ...rest
  }: DrawerProps) => {
    const id = useId();
    const prefersReducedMotion = usePrefersReducedMotion();
    const isMobile = useBreakpoint('sm');
    const [isExpanded, toggleIsExpanded] = useToggle(false);
    const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });
    const baseStyle = useMemo(
      () =>
        sanitizeCSSVariables({
          ...style,
          ...buildResponsiveProps(componentStyleName, 'container-padding', gap, 'space'),
        }),
      [style, gap],
    );
    const progressAsPercentage = useMemo(() => {
      if (progress === undefined) {
        return undefined;
      }

      return Math.min(Math.max(progress, 0), 1) * 100;
    }, [progress]);

    const shouldAnimate = !prefersReducedMotion && !withoutAnimation;

    // Pinch the root content when a drawer is open, revert when all drawers are closed
    useLayoutEffect(() => {
      if (isOpen) {
        document.body.classList.add(dialogStyles.pinchedContent);
      }

      return () => {
        if (!findPinchableDialogs().length) {
          document.body.classList.remove(dialogStyles.pinchedContent);
        }
      };
    }, [isOpen]);

    const leftActionElement = <Button affixIcon={closeIcon} onMouseDown={closeIconAction} size="small" variant="secondary" />;
    const progressStyle = useMemo(
      () => ({
        width: `${progressAsPercentage ?? 0}%`,
      }),
      [progressAsPercentage],
    );

    const progressBar = progressAsPercentage !== undefined ? <div className={styles.progressBar} style={progressStyle} /> : null;

    const dialogContent = (
      <>
        {progressBar}

        <DialogHeader
          leftAction={
            closeIconAction ? (
              leftActionElement
            ) : (
              <DialogPrimitive.Close aria-label="Close drawer" asChild className={styles.close} onMouseDown={handleCloseMouseDown}>
                {leftActionElement}
              </DialogPrimitive.Close>
            )
          }
          rightAction={
            titleRightAction ??
            (canExpand ? (
              <Button
                affixIcon={isExpanded ? <CollapseIcon aria-hidden /> : <ExpandIcon aria-hidden />}
                aria-label={isExpanded ? 'Collapse' : 'Expand to full-width'}
                className={styles.expandButton}
                onMouseDown={toggleIsExpanded}
                size="small"
                variant="secondary"
              />
            ) : null)
          }
          title={title ? <DialogPrimitive.Title>{title}</DialogPrimitive.Title> : null}
        />
        <div className={styles.contentContainer}>
          <DialogPrimitive.Description className={clsx(styles.description, typeof description === 'string' && styles.visuallyHidden)}>
            {typeof description === 'string' ? description : 'rendered' in description && description.rendered ? description.rendered : null}
          </DialogPrimitive.Description>
          {children}
        </div>
      </>
    );

    const baseDialogClassName = clsx(
      dialogStyles.dialog,
      styles.drawer,
      styles.container,
      withoutOverlay && styles.withoutOverlay,
      canExpand && isExpanded ? styles.isExpanded : undefined,
      dialogClassName,
    );
    const overlayClassName = clsx(styles.overlay, withoutOverlay && styles.withoutOverlay);

    const contentAnimationVariants = useMemo(
      () => ({
        animate: isMobile ? { y: 0 } : { x: 0 },
        exit: isMobile ? { y: '100%' } : { x: 'calc(100% + 14px)' },
        initial: isMobile ? { y: '100%' } : { x: 'calc(100% + 14px)' },
      }),
      [isMobile],
    );

    return (
      <TypographyProviderReset>
        <DialogPrimitive.Root modal={modal} onOpenChange={onOpenChange} open={isOpen}>
          <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>

          {shouldAnimate ? (
            <AnimatePresence>
              {isOpen ? (
                <DialogPrimitive.Portal forceMount key={id}>
                  <DialogPrimitive.Overlay asChild>
                    <motion.div {...overlayAnimationVariants} className={overlayClassName} key={`${id}-overlay`} transition={overlayTransition} />
                  </DialogPrimitive.Overlay>

                  <DialogPrimitive.Content asChild>
                    <motion.div
                      {...dataAttrProps}
                      {...contentAnimationVariants}
                      className={baseDialogClassName}
                      key={`${id}-content`}
                      ref={ref as Ref<HTMLDivElement>}
                      style={baseStyle}
                      transition={drawerContentTransition}
                      {...rest}
                    >
                      {dialogContent}
                    </motion.div>
                  </DialogPrimitive.Content>
                </DialogPrimitive.Portal>
              ) : null}
            </AnimatePresence>
          ) : (
            <DialogPrimitive.Portal>
              <DialogPrimitive.Overlay className={overlayClassName} />

              <DialogPrimitive.Content
                {...dataAttrProps}
                className={clsx(baseDialogClassName, styles.withoutAnimation)}
                ref={ref as Ref<HTMLDivElement>}
                style={baseStyle}
                {...rest}
              >
                {dialogContent}
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </DialogPrimitive.Root>
      </TypographyProviderReset>
    );
  },
);
